/**
 * A home page's cards, as data: what a card declares, what it makes of the answer its route gave,
 * and how the arrangement changes when somebody moves or resizes one. Pure — no React, no network —
 * so every rule here is tested without a browser, and the components in home.tsx only draw it.
 *
 * A card is a declaration, never code: `packages/schema/schemas/home-card.schema.json` is the
 * contract, the assistant proposes against it, and identity checks every card on save. The type
 * below is that schema written by hand, because this package is loaded by a browser and carries no
 * dependencies (the validator needs Ajv and the file system); the shape is the same, and a card that
 * reached here has passed the schema on its way in.
 *
 * The one rule the figures follow is the lists' rule: they are never worked out from a page. A
 * route that answers a plain array answered everything, and the browser may filter, group and
 * count it. A route that answers `{rows, total, totals}` answered one page: a count reads `total`,
 * a sum reads the server's `totals`, and anything that would group, filter or sort the page is
 * refused with a reason rather than drawn wrong.
 */
/** The limits a home page keeps: the grid, a card's size, how many, and how often it reads. */
export const HOME_LIMITS = {
    columns: 4, rowHeight: 118, gap: 12,
    width: { min: 1, max: 4 }, height: { min: 1, max: 3 },
    pages: 5, cards: 12, tableColumns: 5,
    refresh: { min: 60, max: 3600, default: 300 },
    /** Shown when the card says no `limit`: rows of a table, bars, points of a trend. */
    shown: { table: 5, bars: 6, trend: 30 },
};
// ── Reading a row ───────────────────────────────────────────────────────────────────────────
/**
 * The value at a dotted path — `employee.department.name` — or undefined. Only a row's own keys
 * are followed, so a path can never reach `__proto__`, `constructor` or anything a row inherits.
 */
export function readPath(row, path) {
    let at = row;
    for (const part of path.split('.')) {
        if (at === null || typeof at !== 'object' || Array.isArray(at))
            return undefined;
        if (!Object.prototype.hasOwnProperty.call(at, part))
            return undefined;
        at = at[part];
    }
    return at;
}
/** A figure, when the value is one: a number, or a string holding one («31200.00», as money often travels). */
function figureOf(value) {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
const absent = (v) => v === null || v === undefined || v === '';
/** Two values in order: as figures when both are, else as words; an absent value last. */
function compare(a, b) {
    if (absent(a) || absent(b))
        return absent(a) === absent(b) ? 0 : absent(a) ? 1 : -1;
    const x = figureOf(a);
    const y = figureOf(b);
    if (x !== null && y !== null)
        return x - y;
    const s = String(a);
    const t = String(b);
    return s < t ? -1 : s > t ? 1 : 0;
}
/** Whether a row's value meets one condition. `contains` is case-blind, and on a list asks whether it holds the value. */
export function meets(value, c) {
    const same = (v, w) => {
        if (absent(v))
            return false;
        const x = figureOf(v);
        const y = figureOf(w);
        if (x !== null && y !== null && typeof v !== 'boolean' && typeof w !== 'boolean')
            return x === y;
        return String(v) === String(w);
    };
    switch (c.op) {
        case 'eq': return !Array.isArray(c.value) && same(value, c.value);
        case 'ne': return !Array.isArray(c.value) && !same(value, c.value);
        case 'in': return Array.isArray(c.value) && c.value.some((w) => same(value, w));
        case 'contains': {
            if (Array.isArray(c.value))
                return false;
            const needle = String(c.value).toLowerCase();
            if (Array.isArray(value))
                return value.some((v) => String(v).toLowerCase() === needle);
            return !absent(value) && String(value).toLowerCase().includes(needle);
        }
        default: {
            if (absent(value) || Array.isArray(c.value))
                return false;
            const d = compare(value, c.value);
            return c.op === 'gt' ? d > 0 : c.op === 'gte' ? d >= 0 : c.op === 'lt' ? d < 0 : d <= 0;
        }
    }
}
/** A group's figure: its count, or an operation over one field's numbers (absent values left out). Null: nothing to measure. */
function measure(rows, m) {
    const op = m?.op ?? 'count';
    if (op === 'count' || !m?.field)
        return { ok: true, value: rows.length };
    const values = [];
    for (const row of rows) {
        const v = readPath(row, m.field);
        if (absent(v))
            continue;
        const n = figureOf(v);
        if (n === null)
            return { ok: false, field: m.field };
        values.push(n);
    }
    if (op === 'sum')
        return { ok: true, value: values.reduce((a, b) => a + b, 0) };
    if (!values.length)
        return { ok: true, value: null };
    if (op === 'avg')
        return { ok: true, value: values.reduce((a, b) => a + b, 0) / values.length };
    return { ok: true, value: op === 'min' ? Math.min(...values) : Math.max(...values) };
}
/** A group's key: the value's words, a day for a moment (so a trend is by day), '' for no value. Null: not one value. */
function groupKey(value, byDay) {
    if (absent(value))
        return '';
    if (typeof value === 'object')
        return null;
    const text = String(value);
    if (byDay && /^\d{4}-\d{2}-\d{2}T/.test(text))
        return text.slice(0, 10);
    return text;
}
function group(rows, by, m, byDay) {
    const buckets = new Map();
    for (const row of rows) {
        const key = groupKey(readPath(row, by), byDay);
        if (key === null)
            return { ok: false, reason: 'not_groupable', field: by };
        const bucket = buckets.get(key);
        if (bucket)
            bucket.push(row);
        else
            buckets.set(key, [row]);
    }
    const groups = [];
    for (const [key, members] of buckets) {
        const measured = measure(members, m);
        if (!measured.ok)
            return { ok: false, reason: 'not_a_number', field: measured.field };
        // A group with no value to average has no bar, rather than a bar at nought that says it had.
        if (measured.value !== null)
            groups.push({ key, value: measured.value });
    }
    return { ok: true, groups };
}
// ── The answer ──────────────────────────────────────────────────────────────────────────────
/** The server's figure for the whole list: `totals.stats[key]` as the lists answer it, or `totals[key]`. */
function serverTotal(totals, key) {
    if (!totals || typeof totals !== 'object')
        return null;
    const t = totals;
    const stats = t['stats'] && typeof t['stats'] === 'object' ? t['stats'][key] : undefined;
    return typeof stats === 'number' ? stats : typeof t[key] === 'number' ? t[key] : null;
}
/**
 * What a card makes of the answer its route gave. Filters, groups, measures, sorts and limits a
 * complete answer (a plain array); follows the paged rules for `{rows, total, totals}`; refuses,
 * with a reason, anything that would be drawn wrong. Pure.
 *
 * Over a paged answer: a count is `total`; a `sum` is the server's figure under the field's name,
 * and `avg`/`min`/`max` under `avg_<field>`, `min_<field>`, `max_<field>` (in `totals.stats` as a
 * list answers them, or in `totals` itself) — absent, the card says the figure is not available.
 * A table shows the page's first rows as the server ordered them, and `more` is the rest of `total`.
 */
export function evaluateCard(card, answer) {
    const paged = !Array.isArray(answer) && answer !== null && typeof answer === 'object' && Array.isArray(answer.rows);
    if (!Array.isArray(answer) && !paged)
        return { kind: 'refused', reason: 'not_a_list' };
    const all = (paged ? answer.rows : answer);
    const filters = card.filter ?? [];
    if (paged) {
        if (card.shape === 'bars' || card.shape === 'trend')
            return { kind: 'refused', reason: 'paged_grouping' };
        if (filters.length)
            return { kind: 'refused', reason: 'paged_filter' };
        if (card.shape === 'table' && card.sort)
            return { kind: 'refused', reason: 'paged_sort' };
    }
    const rows = filters.length ? all.filter((row) => filters.every((c) => meets(readPath(row, c.field), c))) : all;
    switch (card.shape) {
        case 'number': {
            const op = card.measure?.op ?? 'count';
            if (paged) {
                const page = answer;
                if (op === 'count') {
                    return typeof page.total === 'number' ? { kind: 'number', value: page.total, basis: 'total' } : { kind: 'refused', reason: 'no_total' };
                }
                const field = card.measure?.field ?? '';
                const figure = serverTotal(page.totals, op === 'sum' ? field : `${op}_${field}`);
                return figure === null ? { kind: 'refused', reason: 'no_totals', field } : { kind: 'number', value: figure, basis: 'totals' };
            }
            const measured = measure(rows, card.measure);
            if (!measured.ok)
                return { kind: 'refused', reason: 'not_a_number', field: measured.field };
            return measured.value === null ? { kind: 'empty' } : { kind: 'number', value: measured.value, basis: 'rows' };
        }
        case 'bars': {
            const grouped = group(rows, card.group_by ?? '', card.measure, false);
            if (!grouped.ok)
                return { kind: 'refused', reason: grouped.reason, field: grouped.field };
            // The largest first, unless the card says otherwise: by the group's own name when it sorts on
            // the grouped field, else by the figure its way. Ties by name, so the order never flickers.
            const byName = card.sort !== undefined && card.sort.field === card.group_by;
            const sign = card.sort?.dir === 'asc' ? 1 : -1;
            const sorted = [...grouped.groups].sort((a, b) => {
                // The group of rows with no value stays last whichever way the names run.
                if (byName)
                    return a.key === '' || b.key === '' ? compare(a.key, b.key) : sign * compare(a.key, b.key);
                return sign * (a.value - b.value) || compare(a.key, b.key);
            });
            const limit = card.limit ?? HOME_LIMITS.shown.bars;
            if (!sorted.length)
                return { kind: 'empty' };
            return { kind: 'bars', items: sorted.slice(0, limit).map((g) => ({ key: g.key, label: g.key, value: g.value })), more: Math.max(0, sorted.length - limit) };
        }
        case 'trend': {
            const grouped = group(rows, card.x ?? '', card.measure, true);
            if (!grouped.ok)
                return { kind: 'refused', reason: grouped.reason, field: grouped.field };
            // Time runs one way: oldest first, and the latest `limit` points kept. A row with no x has no place on it.
            const points = grouped.groups.filter((g) => g.key !== '').sort((a, b) => compare(a.key, b.key));
            const limit = card.limit ?? HOME_LIMITS.shown.trend;
            if (!points.length)
                return { kind: 'empty' };
            return { kind: 'trend', points: points.slice(-limit).map((g) => ({ key: g.key, label: g.key, value: g.value })) };
        }
        case 'table': {
            const sorted = card.sort
                ? [...rows].sort((a, b) => {
                    const va = readPath(a, card.sort.field);
                    const vb = readPath(b, card.sort.field);
                    // An absent value stays last whichever way the rest runs.
                    if (absent(va) || absent(vb))
                        return compare(va, vb);
                    return (card.sort.dir === 'asc' ? 1 : -1) * compare(va, vb);
                })
                : rows;
            const limit = card.limit ?? HOME_LIMITS.shown.table;
            const shown = sorted.slice(0, limit).filter((r) => r !== null && typeof r === 'object' && !Array.isArray(r));
            if (!shown.length)
                return { kind: 'empty' };
            const total = paged && typeof answer.total === 'number' ? answer.total : sorted.length;
            return { kind: 'table', columns: (card.columns ?? []).slice(0, HOME_LIMITS.tableColumns), rows: shown, more: Math.max(0, total - shown.length) };
        }
    }
}
/** How often a card reads its route, in seconds: its own choice, kept between one minute and an hour. */
export function refreshSeconds(card) {
    const s = card.refresh_seconds ?? HOME_LIMITS.refresh.default;
    return Math.min(HOME_LIMITS.refresh.max, Math.max(HOME_LIMITS.refresh.min, Math.round(s)));
}
// ── Arranging ───────────────────────────────────────────────────────────────────────────────
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
/** A size kept inside the grid: 1–4 wide, 1–3 high. */
export function clampSize(size) {
    return {
        w: clamp(Math.round(size.w), HOME_LIMITS.width.min, HOME_LIMITS.width.max),
        h: clamp(Math.round(size.h), HOME_LIMITS.height.min, HOME_LIMITS.height.max),
    };
}
/** The card moved to `to` in the order (clamped to the list); the others keep theirs. The same list when nothing moves. */
export function placeCard(cards, id, to) {
    const from = cards.findIndex((c) => c.id === id);
    if (from < 0)
        return cards;
    const target = clamp(to, 0, cards.length - 1);
    if (target === from)
        return cards;
    const next = [...cards];
    const [card] = next.splice(from, 1);
    next.splice(target, 0, card);
    return next;
}
/** The card one place earlier (`-1`) or later (`+1`) in the order. */
export function moveCard(cards, id, delta) {
    const from = cards.findIndex((c) => c.id === id);
    return from < 0 ? cards : placeCard(cards, id, from + delta);
}
/** The card's size set, kept inside the grid. The same list when the size does not change. */
export function sizeCard(cards, id, size) {
    const at = cards.findIndex((c) => c.id === id);
    if (at < 0)
        return cards;
    const next = clampSize(size);
    const was = cards[at].size;
    if (was.w === next.w && was.h === next.h)
        return cards;
    return cards.map((c, i) => (i === at ? { ...c, size: next } : c));
}
/** The card grown or shrunk by whole columns and rows. */
export function resizeCard(cards, id, dw, dh) {
    const card = cards.find((c) => c.id === id);
    return card ? sizeCard(cards, id, { w: card.size.w + dw, h: card.size.h + dh }) : cards;
}
/**
 * What a key does to the focused card in the arrangement, or null when the key is not one of these.
 * The order runs the way the page is read, so in Arabic the right arrow is «earlier»:
 * - an arrow moves the card one place — the reading-forward arrow and ↓ later, the other two earlier;
 * - Shift with an arrow resizes it from its far corner — the reading-forward arrow wider, the other
 *   narrower, ↓ taller, ↑ shorter.
 */
export function keyEdit(cards, id, key, shift, rtl) {
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    if (key !== forward && key !== back && key !== 'ArrowUp' && key !== 'ArrowDown')
        return null;
    if (shift) {
        if (key === forward)
            return resizeCard(cards, id, 1, 0);
        if (key === back)
            return resizeCard(cards, id, -1, 0);
        return resizeCard(cards, id, 0, key === 'ArrowDown' ? 1 : -1);
    }
    return moveCard(cards, id, key === forward || key === 'ArrowDown' ? 1 : -1);
}
/**
 * The size a corner dragged by (dx, dy) pixels gives, in whole columns and rows of the grid it is in.
 * `dx` is the screen's; in a right-to-left page the far corner is on the left, so dragging left widens.
 * `gridWidth` is the grid's own width, from which a column's is worked out.
 */
export function sizeFromDrag(start, dx, dy, gridWidth, rtl) {
    const { columns, gap, rowHeight } = HOME_LIMITS;
    const column = Math.max(1, (gridWidth - gap * (columns - 1)) / columns);
    const across = (rtl ? -dx : dx) / (column + gap);
    const down = dy / (rowHeight + gap);
    return clampSize({ w: start.w + Math.round(across), h: start.h + Math.round(down) });
}
/** The ready-made sizes a card's menu offers, as the design draws them. */
export const HOME_PRESET_SIZES = [{ w: 1, h: 1 }, { w: 2, h: 1 }, { w: 2, h: 2 }, { w: 4, h: 2 }];
/** A size as its chip writes it: «2×1», width first. */
export function sizeWords(size) {
    return `${size.w}×${size.h}`;
}
export function templateContext(result) {
    switch (result.kind) {
        case 'number': return { value: result.value, count: result.value };
        case 'bars': return { items: result.items, count: result.items.length, more: result.more };
        case 'trend': return { items: result.points, count: result.points.length };
        case 'table': return { rows: result.rows, count: result.rows.length + result.more, more: result.more, total: result.rows.length + result.more };
        default: return {};
    }
}
/** Rows handed to a script, at most: the answer's own, kept by the card's filter when it answered everything; a page as it came. */
export const SCRIPT_ROWS = 500;
export function scriptData(card, answer) {
    const paged = !Array.isArray(answer) && answer !== null && typeof answer === 'object' && Array.isArray(answer.rows);
    const all = (paged ? answer.rows : Array.isArray(answer) ? answer : []);
    const filters = card.filter ?? [];
    const rows = !paged && filters.length ? all.filter((row) => filters.every((c) => meets(readPath(row, c.field), c))) : all;
    const total = paged ? (typeof answer.total === 'number' ? answer.total : null) : rows.length;
    return { rows: rows.slice(0, SCRIPT_ROWS), total, paged, result: evaluateCard(card, answer) };
}
//# sourceMappingURL=home-model.js.map